import type { IButton } from "@Components/Button";
import { Button } from "@Components/Button";
import { defaultAttributes } from "@Components/Component";
import type { IComponentAjax, IComponentAjaxState } from "@Components/ComponentAjax";
import { ComponentAjax } from "@Components/ComponentAjax";
import { ErrorContainer } from "@Components/ErrorContainer";
import { Modal } from "@Components/Modal";
import { AjaxMethod } from "@Components/Modules/Ajax";
import type { IErrors } from "@Components/Modules/ErrorHandler";
import { ErrorType } from "@Components/Modules/ErrorHandler";
import { htmlEncode, urlFormat } from "@Components/Modules/Helpers";
import { isNullOrEmpty, isTextTooLong, isTextTooShort } from "@Components/Modules/Validations";
import type { ITextbox } from "@Components/Textbox";
import { Textbox } from "@Components/Textbox";
import type { IValidator } from "@Components/Validator";
import { Discount } from "@Routes/DiscountLinks";

import * as css from "./DiscountReviewOpinion.module.css";

interface IDiscountReviewBase extends IComponentAjax<boolean> {
    title: string;
    review?: ITextbox;
    submitButton?: IButton;
    reviewTooShortError?: IValidator;
    reviewTooLongError?: IValidator;
}

export interface IDiscountReviewOpinion extends IDiscountReviewBase {
    reviewRequiredError?: IValidator;
    onClose: () => void;
    onCommentAdded?: (value: number) => void;
}

interface IDiscountReviewOpinionState extends IComponentAjaxState {
    isOpen: boolean;
    comment?: string;
}

export class DiscountReviewOpinion extends ComponentAjax<
    boolean,
    IDiscountReviewOpinion,
    IDiscountReviewOpinionState
> {
    public static defaultProps: IDiscountReviewOpinion = {
        ...ComponentAjax.defaultProps,
        title: null,
        review: null,
        submitButton: null,
        reviewRequiredError: { errorName: "comment-required", message: "Comment is required" },
        reviewTooShortError: { errorName: "comment-short-length", message: "Comment too short" },
        reviewTooLongError: { errorName: "comment-long-length", message: "Comment too long" },
        onClose: null,
        onCommentAdded: null,
    };

    public errors: IErrors = {
        default: { ...this.defaultErrors().default, type: ErrorType.Validator },
        commentRequired: {
            name: "comment-required",
            type: ErrorType.Validator,
            targetRef: "comment-input",
            container: { ref: "comment-error-container" },
            onValidate: () => {
                if (isNullOrEmpty(this.state.comment)) {
                    return false;
                }
                return true;
            },
        },
        commentTooShortError: {
            name: "comment-short-length",
            type: ErrorType.Validator,
            targetRef: "comment-input",
            container: { ref: "comment-error-container" },
            onValidate: () => {
                if (isTextTooShort(this.state.comment, 5) === false) {
                    return false;
                }
                return true;
            },
        },
        commentTooLongError: {
            name: "comment-long-length",
            type: ErrorType.Validator,
            targetRef: "comment-input",
            container: { ref: "comment-error-container" },
            onValidate: () => {
                if (isTextTooLong(this.state.comment, 256) === false) {
                    return false;
                }
                return true;
            },
        },
    };

    constructor(props: IDiscountReviewOpinion) {
        super({
            ...props,
            ajaxAction: urlFormat(Discount, { action: "AddComment" }),
            ajaxMethod: AjaxMethod.Post,
        });

        this.state = {
            isOpen: true,
            comment: "",
            isLoading: false,
        };
    }

    public render() {
        return (
            <Modal
                className={css.discountReviewModal}
                isOpen={this.state.isOpen}
                onClose={this.closeModal}
                closeOnBlur={false}
                closeOnEsc
            >
                <div {...defaultAttributes(this.props, css.discountReviewOpinionComponent)}>
                    {this.renderLoading()}
                    {this.inputModal()}
                    {this.renderAjaxErrorContainer()}
                </div>
            </Modal>
        );
    }

    protected ajaxSuccess(response: boolean) {
        if (response === true) {
            if (this.props.onCommentAdded) {
                this.props.onCommentAdded.call(this, -1);
            }
            this.closeModal();
        }

        this.setState({ isLoading: false });
    }

    protected ajaxFail() {
        this.setState({ isLoading: false });
        return true;
    }

    private closeModal = () => {
        this.setState({ isOpen: false });
        this.props.onClose();
    };

    private submit = () =>
        this.ajaxRequest({ comment: htmlEncode(this.state.comment), id: this.props.id }, () =>
            this.setState({ isLoading: true }),
        );

    private inputModal = () => (
        <div>
            <div className={css.headerTitle}>{this.props.title}</div>
            {this.renderCommentArea()}
        </div>
    );

    private renderCommentArea = () => {
        const onSubmit = (ev: React.FormEvent<HTMLFormElement>): void => {
            ev.preventDefault();
            ev.stopPropagation();
            this.submit();
        };

        return (
            <form className={css.commentArea} onSubmit={onSubmit}>
                <Textbox
                    {...this.props.review}
                    className={css.commentInput}
                    ref="comment-input"
                    value={this.state.comment}
                    onChange={(event) => this.setState({ comment: event.target.value })}
                    disableAutoComplete={true}
                />
                <ErrorContainer {...this.errors.commentRequired.container} />
                <div className={css.footer}>{this.renderButton()}</div>
            </form>
        );
    };

    private renderButton = () =>
        this.props.submitButton && <Button {...this.props.submitButton} onClick={this.submit} />;
}
